// @ts-check
/* eslint-disable import/no-unresolved, sort-imports */
import React, {useEffect, useMemo, useRef} from "react"
import classNames from "classnames"
import Collection from "../collection.js"
import memo from "set-state-compare/build/memo.js"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {simpleObjectDifferent} from "set-state-compare/build/diff-utils.js"
import useModelEvent from "../use-model-event.js"

/**
 * @typedef {object} CheckboxProps
 * @property {boolean} [checked]
 * @property {string} [className]
 * @property {boolean} indeterminate
 * @property {Function} [onChange]
 * @property {object} [style]
 */
/** @typedef {Record<string, never>} CheckboxState */
const Checkbox = memo(shapeComponent(/** @augments {ShapeComponent<CheckboxProps, CheckboxState>} */ class Checkbox extends ShapeComponent {
  render() {
    const {indeterminate, ...restProps} = this.props
    const checkboxRef = useRef(undefined)

    useEffect(() => {
      checkboxRef.current.indeterminate = indeterminate
    })

    return (
      <input ref={checkboxRef} type="checkbox" {...restProps} />
    )
  }
}))

/**
 * @typedef {object} Props
 * @property {object} [currentWorkplace]
 * @property {Collection} [query]
 * @property {object} [style]
 * @property {object} [table]
 */
/**
 * @typedef {object} State
 * @property {boolean} checked
 * @property {boolean} indeterminate
 */
export default memo(shapeComponent(/** @augments {ShapeComponent<Props, State>} */ class ApiMakerTableWorkerPluginsCheckAllCheckbox extends ShapeComponent {
  static propTypes = propTypesExact({
    currentWorkplace: PropTypes.object,
    query: PropTypes.instanceOf(Collection),
    style: PropTypes.object,
    table: PropTypes.object
  })

  state = {
    checked: false,
    indeterminate: false
  }

  updateAllCheckedRequestId = 0

  setup() {
    useMemo(() => {
      this.updateAllChecked()
    }, [])

    useModelEvent(this.props.currentWorkplace, "workplace_links_created", this.tt.onLinksCreated)
    useModelEvent(this.props.currentWorkplace, "workplace_links_destroyed", this.tt.onLinksDestroyed)
  }

  componentDidUpdate(prevProps) {
    const previousParams = prevProps.query.params()
    const currentParams = this.props.query.params()

    if (simpleObjectDifferent(previousParams, currentParams, true)) {
      this.updateAllChecked()
    }
  }

  async updateAllChecked() {
    const requestId = ++this.updateAllCheckedRequestId
    const {query, currentWorkplace, table} = this.props
    const run = table?.withBlocking ?? ((fn) => fn())
    const queryLinksStatusResult = await run(() => currentWorkplace.queryLinksStatus({query}))

    if (requestId !== this.updateAllCheckedRequestId) return

    const allChecked = queryLinksStatusResult.all_checked
    const someChecked = queryLinksStatusResult.some_checked

    this.setState({
      checked: allChecked,
      indeterminate: someChecked
    })
  }

  render() {
    const {className, style} = this.props
    const {checked, indeterminate} = this.s

    return (
      <Checkbox
        checked={checked}
        className={classNames("api-maker--table--worker-plugins-check-all-checkbox", className)}
        indeterminate={indeterminate}
        onChange={this.onCheckedChanged}
        style={style}
      />
    )
  }

  modelClassName = () => this.props.query.modelClass().modelClassData().name

  onCheckedChanged = async (e) => {
    e.preventDefault()

    const {currentWorkplace, query, table} = this.props
    const checkbox = e.target
    const run = table?.withBlocking ?? ((fn) => fn())

    if (checkbox.checked) {
      await run(() => currentWorkplace.addQuery({query}))
      this.setState({checked: true, indeterminate: false})
    } else {
      await run(() => currentWorkplace.removeQuery({query}))
      this.setState({checked: false, indeterminate: false})
    }
  }

  onLinksCreated = ({args}) => {
    if (args.resource_types?.includes(this.modelClassName())) {
      this.updateAllChecked()
    }
  }

  onLinksDestroyed = ({args}) => {
    if (args.resource_types?.includes(this.modelClassName())) {
      this.updateAllChecked()
    }
  }
}))
